import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Form,
  message,
  Modal,
  Space,
  theme,
  Typography,
} from "antd";
import { ArrowRightOutlined, CloseOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  createPartnerThunk,
  editPartnerThunk,
  selectCurrentPartner,
} from "../../partnersSlice";
import {
  getPartnerFromServer,
  partnerFromDetailsPayload,
} from "../../partnersApi";
import {
  IPartner,
  IPartnerDetails,
  PricingTemplate,
} from "../../../../interfaces/partner.interface";
import { OnlinePricingStructure } from "../../../../interfaces/onlinePricing.interface";
import { PlatformType } from "../../../../interfaces/paxTerminal.interface";
import { KlearlyCountryAlpha2Code } from "../../../../interfaces/klearlyCountryAlpha2Code.enum";
import CountryFlag from "../../../common/country-flag/CountryFlag";
import { PARTNER_WIZARD_STEPS, WizardMode } from "./wizardSteps";
import WizardStepper from "../../../common/wizard/WizardStepper";
import PartnerDetailsStep from "./steps/PartnerDetailsStep";
import PartnerTypeStep, {
  optionIdFromPartner,
  PartnerTypeOptionId,
  partnerFieldsFromOption,
} from "./steps/PartnerTypeStep";
import PartnerInPersonPricingStep from "./steps/PartnerInPersonPricingStep";
import PartnerOnlinePricingStep from "./steps/PartnerOnlinePricingStep";
import PartnerCouplingStep from "./steps/PartnerCouplingStep";
import {
  buildInPersonRangeUpdate,
  buildInPersonState,
  buildOnlineState,
  buildOnlineRangeUpdate,
  BucketValidationError,
  hasAnyOnlinePricing,
  inPersonStateToPartnerFields,
  omitOnlinePricingFields,
  InPersonPricingState,
  onlineSaveFields,
  withSavedOnlineVendorFees,
  onlineSaveRefusal,
  OnlinePricingState,
  prioritizeOnlineProfileForView,
  remapLocalMethodForCountry,
  validateInPersonState,
  validateOnlineState,
} from "./steps/partnerPricing";
import { selectHideInPersonAmex } from "../../../feature-flags/featureFlagsSlice";

const { Title, Text } = Typography;

const countryFullName = (alpha2?: string) => {
  if (!alpha2) return "";
  const entry = Object.entries(KlearlyCountryAlpha2Code).find(
    ([, code]) => code === alpha2
  );
  return entry ? entry[0] : alpha2.toUpperCase();
};

// Order-insensitive structural equality for dirty checks (baselines and live
// section state are plain JSON-shaped values).
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => deepEqual(aObj[k], bObj[k]));
};

type TypeDraft = {
  partnerTypeOption?: PartnerTypeOptionId;
  softwareVendorId?: string;
};

// Loose mirror of the Details form so cross-tab dirty checks and re-seeding work
// even when the antd form is unmounted on another step.
type DetailsDraft = {
  country?: KlearlyCountryAlpha2Code;
  name?: string;
  ownerName?: string;
  email?: string;
  phone?: { countryCode?: string; number?: string };
};

const detailsFromPartner = (p?: Partial<IPartner>): DetailsDraft => ({
  country: p?.country,
  name: p?.name,
  ownerName: p?.ownerName,
  email: p?.email,
  phone: {
    countryCode: p?.phone?.countryCode ?? "31",
    number: p?.phone?.number,
  },
});

const typeFromPartner = (p?: Partial<IPartner>): TypeDraft => ({
  partnerTypeOption: optionIdFromPartner(p),
  softwareVendorId:
    typeof p?.softwareVendorId === "string" ? p.softwareVendorId : undefined,
});

interface PartnerWizardProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  mode: WizardMode;
}

const LAST_STEP = PARTNER_WIZARD_STEPS.length - 1;

// A partner created today is priced on sixteen buckets: it is the structure the
// merchant, deal and mapping surfaces are built around. Existing partners keep
// whatever they are stored on — buildOnlineState reads that off their groups.
const CREATE_ONLINE_STRUCTURE = OnlinePricingStructure.SixteenBucket;

// A save the backend refused, as it came back. Held at wizard level because the
// create flow submits from the last step, where the online step is not mounted.
type SaveError = { message?: string; code?: string };

// Computed by the partner-details read alone: the list the wizard is opened
// from does not carry them, so they are fetched rather than read off Redux.
type PartnerOnlineDefaults = Pick<
  IPartner,
  | "hasMerchantsSubmittedToAdyen"
  | "defaultOnlinePriceRange"
  | "onlinePurchPriceProfiles"
>;

const onlineRangeId = (value: unknown): string => {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object" && "$oid" in value) {
    const oid = (value as { $oid?: unknown }).$oid;
    if (typeof oid === "string" && oid) return oid;
  }
  return "";
};

const ONLINE_STEP = 3;

// The codes the backend refuses an online section with. The message test picks
// up the same refusals when the code is dropped in serialization: every one of
// them names the section it is about (`defaultOnlinePricing`, ...).
const ONLINE_PRICING_ERROR_CODES = new Set([
  "INVALID_ONLINE_PRICING",
  "ONLINE_PRICING_COUNTRY_MISMATCH",
  "ONLINE_PRICING_STRUCTURE_DOWNGRADE",
  // Names the range and says a separate range is needed for the other
  // structure, which is the whole of what the operator has to act on.
  "ONLINE_PRICING_STRUCTURE_CHANGE_NOT_ALLOWED",
]);

const isOnlinePricingError = (error: SaveError): boolean =>
  (!!error.code && ONLINE_PRICING_ERROR_CODES.has(error.code)) ||
  /onlinepricing/i.test(error.message ?? "");

const saveErrorFrom = (error?: {
  message?: string;
  code?: string;
}): SaveError => ({
  // Backend validation messages name the section and the groups at fault and
  // are written for an operator; anything longer is not one of those.
  message:
    error?.message && error.message.length <= 300 ? error.message : undefined,
  code: error?.code,
});

export default function PartnerWizard({
  visible,
  setVisible,
  mode,
}: PartnerWizardProps) {
  const { token } = theme.useToken();
  const dispatch = useAppDispatch();
  const currentPartner = useAppSelector(selectCurrentPartner);
  const hideInPersonAmex = useAppSelector(selectHideInPersonAmex);
  const includeAmex = !hideInPersonAmex;
  const [detailsForm] = Form.useForm();
  const [typeForm] = Form.useForm();
  const [step, setStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const [loading, setLoading] = useState(false);
  const [closePrompt, setClosePrompt] = useState(false);
  const [draft, setDraft] = useState<Partial<IPartner>>({});
  const [inPerson, setInPersonReactState] = useState<InPersonPricingState>(() =>
    buildInPersonState(undefined, { includeAmex: true })
  );
  const [online, setOnlineReactState] = useState<OnlinePricingState>(() =>
    buildOnlineState(undefined, undefined, {
      structure: CREATE_ONLINE_STRUCTURE,
    })
  );
  const [onlineSaveError, setOnlineSaveError] = useState<SaveError>();
  const [inPersonSaveError, setInPersonSaveError] = useState<SaveError>();
  const [onlineDefaults, setOnlineDefaults] = useState<PartnerOnlineDefaults>();
  const liveInPersonRef = useRef(inPerson);
  const liveOnlineRef = useRef(online);
  const [coupling, setCoupling] = useState<PlatformType | undefined>(undefined);
  // Details/Type values are mirrored into wizard state so edits survive tab
  // switches (the antd forms unmount on other steps); see ../agents notes.
  const [detailsDraft, setDetailsDraft] = useState<DetailsDraft>({});
  const [typeDraft, setTypeDraft] = useState<TypeDraft>({});
  // "New partner price range" saves the new range immediately (its own
  // editPartnerThunk call, see handleInPersonProfileCreate) so it survives
  // closing the wizard without touching the main Save button.
  const [creatingRange, setCreatingRange] = useState(false);
  // Open state of the current "New partner price range" form; blocks the
  // footer Save & continue so the user finishes or cancels the range first.
  const [newRangeOpen, setNewRangeOpen] = useState(false);

  // inPersonSaveError only ever describes a draft range that Create submitted,
  // so closing the form retires it with the draft — otherwise Cancel leaves
  // "In-person pricing was not saved" sitting over the selected range, which
  // was never sent anywhere. Stable identity: the step reports visibility from
  // an effect keyed on this callback, and a fresh one each render would make
  // that effect toggle newRangeOpen on every commit.
  const handleInPersonNewRangeVisibleChange = useCallback(
    (visible: boolean): void => {
      setNewRangeOpen(visible);
      if (!visible) setInPersonSaveError(undefined);
    },
    []
  );

  // Refs update synchronously before React commits state.
  // Save can then read an InputNumber change from the same event sequence.
  // All pricing writes must use these wrappers; raw setters stay private to them.
  const updateInPersonState = (next: InPersonPricingState): void => {
    liveInPersonRef.current = next;
    setInPersonReactState(next);
    setInPersonSaveError(undefined);
  };
  const updateOnlineState = (next: OnlinePricingState): void => {
    liveOnlineRef.current = next;
    setOnlineReactState(next);
    // A rejection describes the payload that was sent, so it stops describing
    // anything the moment the operator changes one.
    setOnlineSaveError(undefined);
  };

  // Edit-mode dirty baselines: skip re-persisting a step the user merely walked
  // through without changing.
  const initialDetailsRef = useRef<DetailsDraft>({});
  const initialTypeRef = useRef<TypeDraft>({});
  const initialInPersonRef = useRef<InPersonPricingState | null>(null);
  const initialOnlineRef = useRef<OnlinePricingState | null>(null);
  const initialCouplingRef = useRef<PlatformType | undefined>(undefined);
  // The profileId currently persisted as index 0 in purchPriceProfiles.
  // Deliberately NOT updated by a mere dropdown switch (applyInPersonProfile) —
  // only by a successful commitInPerson save — so that switching to a
  // different, unedited range still triggers a save (reorder) instead of the
  // deepEqual check below silently treating "no field changed" as "nothing to save."
  const savedActiveProfileIdRef = useRef<string | undefined>(undefined);
  const savedActiveOnlineProfileIdRef = useRef<string | undefined>(undefined);

  const watchedName = Form.useWatch("name", detailsForm);
  const watchedCountry = Form.useWatch("country", detailsForm);
  const watchedTypeOption = Form.useWatch("partnerTypeOption", typeForm) as
    PartnerTypeOptionId | undefined;
  const watchedVendorId = Form.useWatch("softwareVendorId", typeForm) as
    string | undefined;

  const partnerInContext: Partial<IPartner> =
    mode === "edit" ? currentPartner : draft;
  const showVendorPricing = Boolean(
    mode === "edit" ? typeDraft.softwareVendorId : draft.softwareVendorId
  );
  // Falls back to the committed draft because antd unmounts step 1's form once the
  // wizard moves past it, so watchedCountry alone goes stale on later steps.
  const headerCountry =
    (mode === "create" ? watchedCountry : undefined) ||
    partnerInContext?.country;

  useEffect(() => {
    if (visible) {
      setStep(0);
      setMaxStepReached(mode === "edit" ? LAST_STEP : 0);
      setDraft({});
      detailsForm.resetFields();
      typeForm.resetFields();
      const initialInPerson = buildInPersonState(
        mode === "edit" ? currentPartner : undefined,
        { includeAmex }
      );
      const initialOnline = buildOnlineState(
        mode === "edit" ? currentPartner : undefined,
        mode === "edit" ? currentPartner?.country : undefined,
        mode === "create" ? { structure: CREATE_ONLINE_STRUCTURE } : {}
      );
      const initialCoupling =
        mode === "edit" ? currentPartner?.terminalCouplingPlatform : undefined;
      const initialDetails = detailsFromPartner(
        mode === "edit" ? currentPartner : undefined
      );
      const initialType =
        mode === "edit" ? typeFromPartner(currentPartner) : {};
      updateInPersonState(initialInPerson);
      updateOnlineState(initialOnline);
      setCoupling(initialCoupling);
      setDetailsDraft(initialDetails);
      setTypeDraft(initialType);
      initialInPersonRef.current = initialInPerson;
      savedActiveProfileIdRef.current = initialInPerson.profileId;
      initialOnlineRef.current = initialOnline;
      savedActiveOnlineProfileIdRef.current = initialOnline.profileId;
      initialCouplingRef.current = initialCoupling;
      initialDetailsRef.current = initialDetails;
      initialTypeRef.current = initialType;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, detailsForm, typeForm, includeAmex]);

  useEffect(() => {
    setOnlineDefaults(undefined);
    if (!visible || mode !== "edit" || !currentPartner?._id) return;
    let cancelled = false;
    getPartnerFromServer(currentPartner._id)
      .then((response) => {
        if (!cancelled) setOnlineDefaults(partnerFromDetailsPayload(response));
      })
      // Nothing to fall back to and nothing to say: the step then shows the
      // stored structure frozen, exactly as it did before this read existed.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [visible, mode, currentPartner?._id]);

  // The read lands after the step has been seeded, so a partner with no stored
  // range is moved onto the offered shape once it arrives — never over an edit,
  // and never over a stored range, which is always read as itself.
  useEffect(() => {
    const defaultOnlinePriceRange = onlineDefaults?.defaultOnlinePriceRange;
    if (!defaultOnlinePriceRange) return;
    if (currentPartner?.onlinePurchPriceProfiles?.length) return;
    if (!deepEqual(liveOnlineRef.current, initialOnlineRef.current)) return;
    const next = buildOnlineState(
      { ...currentPartner, defaultOnlinePriceRange },
      currentPartner?.country
    );
    updateOnlineState(next);
    initialOnlineRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineDefaults]);

  useEffect(() => {
    const prev = liveInPersonRef.current;
    if (prev.includeAmex === includeAmex) return;
    updateInPersonState({ ...prev, includeAmex });
  }, [includeAmex]);

  // Nested ids arrive as hex strings or `{ $oid }`; either must match the
  // selected range, otherwise an unused sibling's false unlocks a frozen one.
  const activeRangeSubmittedToAdyen = useMemo(() => {
    const profiles = onlineDefaults?.onlinePurchPriceProfiles;
    const activeId = onlineRangeId(online.profileId);
    const stored =
      (activeId
        ? profiles?.find((profile) => onlineRangeId(profile._id) === activeId)
        : undefined) ??
      profiles?.find((profile) => profile.name === online.profileName);
    return (
      stored?.hasMerchantsSubmittedToAdyen ??
      onlineDefaults?.hasMerchantsSubmittedToAdyen
    );
  }, [onlineDefaults, online.profileId, online.profileName]);

  // Re-derive the online pricing template as the admin picks a country in
  // step 1, so a Belgian partner sees Bancontact (not iDEAL) by the time
  // they reach the Online pricing step. Keeps working after a bucket has been
  // filled in (create mode is a linear country -> ... -> online-pricing flow,
  // so a country change afterward must not leave the admin stuck on the wrong
  // template with no way out): card buckets carry over unchanged, only the
  // local-method bucket moves to the new template's key.
  useEffect(() => {
    if (mode !== "create" || !visible) return;
    const next = remapLocalMethodForCountry(online, headerCountry);
    if (next !== online) {
      updateOnlineState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, visible, headerCountry]);

  // Mirror live Type-form edits into the draft while step 1 is mounted (card
  // selection goes through setFieldsValue, so onValuesChange wouldn't fire).
  useEffect(() => {
    if (mode !== "edit" || step !== 1) return;
    setTypeDraft((prev) => {
      const next: TypeDraft = {
        partnerTypeOption: watchedTypeOption,
        softwareVendorId: watchedVendorId,
      };
      return deepEqual(prev, next) ? prev : next;
    });
  }, [mode, step, watchedTypeOption, watchedVendorId]);

  useEffect(() => {
    setMaxStepReached((prev) => Math.max(prev, step));
  }, [step]);

  const headerName =
    (mode === "create" ? watchedName : undefined) ||
    partnerInContext?.name ||
    (mode === "create" ? "New partner" : "Partner");

  const isLastStep = step === LAST_STEP;

  const close = () => {
    detailsForm.resetFields();
    typeForm.resetFields();
    setDraft({});
    setStep(0);
    setVisible(false);
  };

  const commitDetails = async (): Promise<boolean> => {
    let values: IPartnerDetails;
    try {
      values = (await detailsForm.validateFields()) as IPartnerDetails;
    } catch {
      return false;
    }

    if (mode === "create") {
      setDraft((prev) => ({ ...prev, ...values }));
      return true;
    }

    if (deepEqual(values, initialDetailsRef.current)) return true;

    setLoading(true);
    const result = (await dispatch(
      editPartnerThunk({ partnerId: currentPartner?._id, update: values })
    )) as { error?: { name?: string } };
    setLoading(false);

    if (result?.error?.name) {
      return false;
    }
    message.success("Partner details saved.");
    initialDetailsRef.current = values;
    setDetailsDraft(values);
    return true;
  };

  const commitType = async (): Promise<boolean> => {
    let values: {
      partnerTypeOption: PartnerTypeOptionId;
      softwareVendorId?: string;
    };
    try {
      values = (await typeForm.validateFields()) as typeof values;
    } catch {
      return false;
    }

    const fields = partnerFieldsFromOption(
      values.partnerTypeOption,
      values.softwareVendorId
    );

    if (mode === "create") {
      setDraft((prev) => ({ ...prev, ...fields }));
      return true;
    }

    const nextType: TypeDraft = {
      partnerTypeOption: values.partnerTypeOption,
      softwareVendorId: fields.softwareVendorId,
    };
    if (deepEqual(nextType, initialTypeRef.current)) return true;

    setLoading(true);
    const result = (await dispatch(
      editPartnerThunk({ partnerId: currentPartner?._id, update: fields })
    )) as { error?: { name?: string } };
    setLoading(false);

    if (result?.error?.name) {
      return false;
    }
    message.success("Partner type saved.");
    initialTypeRef.current = nextType;
    setTypeDraft(nextType);
    return true;
  };

  // In edit, only buckets this session actually changed can block a save, so a
  // legacy out-of-band value doesn't lock the step.
  const blockingInPersonErrors = (
    state: InPersonPricingState
  ): BucketValidationError[] => {
    const errors = validateInPersonState(state);
    if (mode !== "edit") return errors;
    return errors.filter(
      (e) =>
        !deepEqual(
          state.buckets[e.key],
          initialInPersonRef.current?.buckets[e.key]
        )
    );
  };

  const commitInPerson = async (): Promise<boolean> => {
    const currentInPerson = liveInPersonRef.current;
    if (
      mode === "edit" &&
      deepEqual(currentInPerson, initialInPersonRef.current) &&
      currentInPerson.profileId === savedActiveProfileIdRef.current
    ) {
      return true;
    }
    // The new-range form is still open; Save must not persist until Create or Cancel.
    if (newRangeOpen) {
      message.error("Finish or cancel the new price range first.");
      return false;
    }
    const blockingInPerson = blockingInPersonErrors(currentInPerson);
    if (blockingInPerson.length > 0) {
      message.error(
        "Please fix the highlighted pricing values before continuing."
      );
      return false;
    }

    const fields = inPersonStateToPartnerFields(
      currentInPerson,
      mode === "edit" ? currentPartner : draft
    );

    if (mode === "create") {
      setDraft((prev) => ({ ...prev, ...fields }));
      return true;
    }

    setLoading(true);
    const result = (await dispatch(
      editPartnerThunk({ partnerId: currentPartner?._id, update: fields })
    )) as {
      error?: { name?: string; message?: string; code?: string };
    };
    setLoading(false);

    // A refusal with a code and no name must not read as a successful save.
    if (result?.error) {
      setInPersonSaveError(saveErrorFrom(result.error));
      return false;
    }
    setInPersonSaveError(undefined);
    message.success("In-person pricing saved.");
    initialInPersonRef.current = currentInPerson;
    savedActiveProfileIdRef.current = currentInPerson.profileId;
    return true;
  };

  const commitOnline = async (): Promise<boolean> => {
    const currentOnline = liveOnlineRef.current;
    if (
      mode === "edit" &&
      deepEqual(currentOnline, initialOnlineRef.current) &&
      currentOnline.profileId === savedActiveOnlineProfileIdRef.current
    ) {
      return true;
    }
    if (!hasAnyOnlinePricing(currentOnline)) {
      if (mode === "create") {
        setDraft((prev) => omitOnlinePricingFields(prev));
      }
      return true;
    }
    const onlineErrors = validateOnlineState(currentOnline);
    const blockingOnline =
      mode === "edit"
        ? onlineErrors.filter(
            (e) =>
              e.unconditional ||
              !deepEqual(
                currentOnline.buckets[e.key],
                initialOnlineRef.current?.buckets[e.key]
              )
          )
        : onlineErrors;
    if (blockingOnline.length > 0) {
      message.error(onlineSaveRefusal(currentOnline));
      return false;
    }
    const fields = onlineSaveFields(currentOnline);

    if (mode === "create") {
      setDraft((prev) => ({ ...prev, ...fields }));
      return true;
    }

    setLoading(true);
    const result = (await dispatch(
      editPartnerThunk({ partnerId: currentPartner?._id, update: fields })
    )) as { error?: { name?: string; message?: string; code?: string } };
    setLoading(false);

    // Any rejection, not just one carrying a name: a refused 16-bucket payload
    // persists nothing, so it has to stop the step rather than read as saved.
    if (result?.error) {
      setOnlineSaveError(saveErrorFrom(result.error));
      return false;
    }
    setOnlineSaveError(undefined);
    message.success("Online pricing saved.");
    // The state is not re-read after a save, so a fee written just now has to
    // count as stored, or clearing it later in this session sends nothing.
    if (fields.softwareVendorOnlinePricing !== undefined) {
      const saved = withSavedOnlineVendorFees(currentOnline);
      updateOnlineState(saved);
      initialOnlineRef.current = saved;
    } else {
      initialOnlineRef.current = currentOnline;
    }
    savedActiveOnlineProfileIdRef.current = currentOnline.profileId;
    return true;
  };

  const commitCoupling = async (): Promise<boolean> => {
    if (!coupling) {
      message.error("Please choose a terminal coupling platform.");
      return false;
    }

    if (mode === "create") return true;

    if (coupling === initialCouplingRef.current) return true;

    setLoading(true);
    const result = (await dispatch(
      editPartnerThunk({
        partnerId: currentPartner?._id,
        update: { terminalCouplingPlatform: coupling },
      })
    )) as { error?: { name?: string } };
    setLoading(false);

    if (result?.error?.name) {
      return false;
    }
    message.success("Coupling platform saved.");
    initialCouplingRef.current = coupling;
    return true;
  };

  const commitStep = async (current: number): Promise<boolean> => {
    if (current === 0) return commitDetails();
    if (current === 1) return commitType();
    if (current === 2) return commitInPerson();
    if (current === 3) return commitOnline();
    if (current === 4) return commitCoupling();
    return true;
  };

  const finalizeCreate = async () => {
    const partner: Partial<IPartner> = {
      ...draft,
      terminalCouplingPlatform: coupling,
    };

    setLoading(true);
    const result = await dispatch(createPartnerThunk(partner));
    setLoading(false);

    if (!createPartnerThunk.fulfilled.match(result) || !result.payload?._id) {
      // Create submits the whole partner from the last step, so a refused
      // online section would otherwise fail somewhere the operator cannot see
      // it. Surface it on the step that owns the pricing it is about.
      const error = createPartnerThunk.rejected.match(result)
        ? saveErrorFrom(result.error)
        : undefined;
      if (error && isOnlinePricingError(error)) {
        setOnlineSaveError(error);
        setStep(ONLINE_STEP);
      }
      return;
    }
    message.success("Partner created.");
    close();
  };

  const handlePrimary = async () => {
    const ok = await commitStep(step);
    if (!ok) return;
    if (!isLastStep) {
      setStep((prev) => prev + 1);
      return;
    }
    if (mode === "create") {
      await finalizeCreate();
    } else {
      close();
    }
  };

  const skipOnline = () => {
    setDraft((prev) => omitOnlinePricingFields(prev));
    setStep((prev) => Math.min(LAST_STEP, prev + 1));
  };

  const goToStep = async (target: number) => {
    if (target === step) return;
    // Create gates forward navigation behind a save; edit is a free tabs model,
    // so switching tabs never auto-saves (changes persist in wizard state and
    // are saved explicitly via the per-section Save / banner / close prompt).
    if (mode === "create" && target > step) {
      const ok = await commitStep(step);
      if (!ok) return;
    }
    setStep(target);
  };

  // Edit-only: surface unsaved section changes (banner + stepper dot) by
  // diffing the live section against its last-saved baseline.
  const detailsDirty =
    mode === "edit" && !deepEqual(detailsDraft, initialDetailsRef.current);
  const typeDirty =
    mode === "edit" && !deepEqual(typeDraft, initialTypeRef.current);
  const hasInPersonChanges = (value: InPersonPricingState): boolean =>
    mode === "edit" && !deepEqual(value, initialInPersonRef.current);
  const hasOnlineChanges = (value: OnlinePricingState): boolean =>
    mode === "edit" &&
    (!deepEqual(value, initialOnlineRef.current) ||
      value.profileId !== savedActiveOnlineProfileIdRef.current);
  const inPersonDirty = hasInPersonChanges(inPerson);
  const onlineDirty = hasOnlineChanges(online);
  const couplingDirty =
    mode === "edit" && coupling !== initialCouplingRef.current;

  const dirtySteps = useMemo(
    () => ({
      0: detailsDirty,
      1: typeDirty,
      2: inPersonDirty,
      3: onlineDirty,
      4: couplingDirty,
    }),
    [detailsDirty, typeDirty, inPersonDirty, onlineDirty, couplingDirty]
  );

  const currentSectionDirty =
    (step === 0 && detailsDirty) ||
    (step === 2 && inPersonDirty) ||
    (step === 3 && onlineDirty) ||
    (step === 4 && couplingDirty);

  const saveCurrentSection = async () => {
    await commitStep(step);
  };

  const discardCurrentSection = () => {
    if (step === 0) {
      setDetailsDraft(initialDetailsRef.current);
      detailsForm.setFieldsValue(initialDetailsRef.current);
    }
    if (step === 1) {
      setTypeDraft(initialTypeRef.current);
      typeForm.setFieldsValue({
        partnerTypeOption: initialTypeRef.current.partnerTypeOption,
        softwareVendorId: initialTypeRef.current.softwareVendorId,
      });
    }
    if (step === 2 && initialInPersonRef.current) {
      updateInPersonState(initialInPersonRef.current);
    }
    if (step === 3 && initialOnlineRef.current) {
      updateOnlineState(initialOnlineRef.current);
    }
    if (step === 4) {
      setCoupling(initialCouplingRef.current);
    }
  };

  // Edit is a tabs model: every section (Details/Type included) lives in wizard
  // state, so switching tabs never drops edits; closing with anything unsaved
  // offers save-all vs discard.
  const hasUnsavedChanges = (): boolean =>
    detailsDirty || typeDirty || inPersonDirty || onlineDirty || couplingDirty;

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setClosePrompt(true);
      return;
    }
    close();
  };

  const handleSave = async () => {
    const ok = await commitStep(step);
    if (ok && isLastStep) close();
  };

  const handleSaveAndContinue = async () => {
    const ok = await commitStep(step);
    if (ok) setStep((prev) => prev + 1);
  };

  const applyInPersonProfile = (profileId: string) => {
    const profiles = currentPartner?.purchPriceProfiles;
    if (!profiles) return;
    const target = profiles.find((p) => p._id === profileId);
    if (!target) return;
    const reordered = [target, ...profiles.filter((p) => p._id !== profileId)];
    const next = buildInPersonState(
      {
        ...currentPartner,
        purchPriceProfiles: reordered,
      },
      { includeAmex }
    );
    updateInPersonState(next);
    initialInPersonRef.current = next;
  };

  const handleInPersonProfileSelect = (profileId: string) => {
    if (!hasInPersonChanges(liveInPersonRef.current)) {
      applyInPersonProfile(profileId);
      return;
    }
    Modal.confirm({
      title: "Switch price range?",
      content:
        "Switching ranges will discard unsaved changes in the current pricing range.",
      okText: "Switch range",
      cancelText: "Keep editing",
      onOk: () => applyInPersonProfile(profileId),
    });
  };

  // Appends the new range and saves it immediately, independent of the wizard's
  // main Save — the range carries its own merchant default and vendor markup, so
  // no pre-existing range moves.
  const handleInPersonProfileCreate = async (
    name: string,
    whitelist: string[],
    draft: InPersonPricingState
  ): Promise<boolean> => {
    if (!currentPartner?._id) return false;
    // Only a bucket that differs from the saved range blocks; New clones unsaved edits too.
    const blockingErrors = blockingInPersonErrors(draft);
    if (blockingErrors.length > 0) {
      message.error(
        "Please fix the highlighted pricing values before creating a range."
      );
      return false;
    }
    setCreatingRange(true);
    const result = (await dispatch(
      editPartnerThunk({
        partnerId: currentPartner._id,
        update: buildInPersonRangeUpdate({
          state: draft,
          existingProfiles: currentPartner.purchPriceProfiles ?? [],
          name,
          whitelist,
        }),
      })
    )) as {
      error?: { name?: string; message?: string; code?: string };
      payload?: IPartner;
    };
    setCreatingRange(false);

    // Any rejection, not just one carrying a name: these refusals serialize
    // with a `code` and no `name`, so keying off the name read a refused create
    // as a successful one and closed the form over it.
    if (result?.error) {
      const refusal = saveErrorFrom(result.error);
      setInPersonSaveError(refusal);
      message.error(
        [refusal.code, refusal.message].filter(Boolean).join(": ") ||
          "Could not create the price range."
      );
      return false;
    }
    setInPersonSaveError(undefined);
    message.success("Price range created.");
    // Rebuild from the FRESH partner (result.payload), not the pre-create
    // snapshot: initialInPersonRef.current.extraProfiles was captured before
    // this save and doesn't contain the range we just created, so reusing it
    // would silently drop that range on the next unrelated save (purchPriceProfiles
    // is a wholesale replace, not a merge). Re-locate the SAME range that was
    // active before Create (by id, not index 0) so we still land back on
    // whichever range was being viewed, not just whatever's first in the array.
    const freshProfiles = result?.payload?.purchPriceProfiles ?? [];
    const activeId = initialInPersonRef.current?.profileId;
    const target = activeId
      ? freshProfiles.find((p) => p._id === activeId)
      : undefined;
    const reordered = target
      ? [target, ...freshProfiles.filter((p) => p._id !== activeId)]
      : freshProfiles;
    const next = buildInPersonState(
      {
        ...result?.payload,
        purchPriceProfiles: reordered,
      },
      { includeAmex }
    );
    updateInPersonState(next);
    initialInPersonRef.current = next;
    // savedActiveProfileIdRef tracks what's actually persisted at index 0. Create
    // APPENDS (never reorders), so index 0 is unchanged — use the real persisted
    // first id, not next.profileId (which is the locally-fronted active range and
    // could still have a pending, not-yet-saved reorder that Save must persist).
    savedActiveProfileIdRef.current = freshProfiles[0]?._id;
    return true;
  };

  const applyOnlineProfile = (profileId: string) => {
    const profiles = currentPartner?.onlinePurchPriceProfiles;
    if (!profiles) return;
    const target = profiles.find((profile) => profile._id === profileId);
    if (!target) return;
    const reordered = [
      target,
      ...profiles.filter((profile) => profile._id !== profileId),
    ];
    const next = buildOnlineState(
      {
        ...currentPartner,
        onlinePurchPriceProfiles: reordered,
      },
      currentPartner?.country
    );
    updateOnlineState(next);
    initialOnlineRef.current = next;
  };

  const handleOnlineProfileSelect = (profileId: string) => {
    if (!hasOnlineChanges(liveOnlineRef.current)) {
      applyOnlineProfile(profileId);
      return;
    }
    Modal.confirm({
      title: "Switch price range?",
      content:
        "Switching ranges will discard unsaved changes in the current pricing range.",
      okText: "Switch range",
      cancelText: "Keep editing",
      onOk: () => applyOnlineProfile(profileId),
    });
  };

  const handleOnlineProfileCreate = async (
    name: string,
    whitelist: string[],
    draft: OnlinePricingState
  ): Promise<boolean> => {
    if (!currentPartner?._id) return false;
    // A four-bucket range with nothing priced serializes to an empty
    // `bucketGroups`, which the backend cannot read a structure off, so it
    // stores a range that prices nothing instead of refusing it.
    if (!hasAnyOnlinePricing(draft)) {
      message.error("Price at least one bucket before creating a range.");
      return false;
    }
    // A stored value out of its own band would otherwise refuse every new range
    // a partner could ever be given, so only a bucket the draft moved blocks —
    // bar an unfloored group, which no stored value explains away.
    const copiedFrom = liveOnlineRef.current;
    const blockingErrors = validateOnlineState(draft).filter(
      (error) =>
        error.unconditional ||
        !deepEqual(draft.buckets[error.key], copiedFrom?.buckets[error.key])
    );
    if (blockingErrors.length > 0) {
      message.error(
        "Please fix the highlighted pricing values before creating a range."
      );
      return false;
    }

    const existingProfiles = currentPartner.onlinePurchPriceProfiles ?? [];
    const activeProfileId = (initialOnlineRef.current ?? liveOnlineRef.current)
      .profileId;
    const update = buildOnlineRangeUpdate({
      state: draft,
      existingProfiles,
      name,
      whitelist,
    });

    setCreatingRange(true);
    const result = (await dispatch(
      editPartnerThunk({
        partnerId: currentPartner._id,
        update,
      })
    )) as {
      error?: { name?: string; message?: string; code?: string };
      payload?: IPartner;
    };
    setCreatingRange(false);

    // Any rejection, not just one carrying a name: these refusals serialize
    // with a `code` and no `name`, so keying off the name read a refused create
    // as a successful one and closed the form over it.
    if (result?.error) {
      const refusal = saveErrorFrom(result.error);
      setOnlineSaveError(refusal);
      // The backend names the range and the groups at fault; a generic toast
      // throws away the only part an operator can act on.
      message.error(refusal.message ?? "Could not create the price range.");
      return false;
    }
    setOnlineSaveError(undefined);
    message.success("Price range created.");
    const freshProfiles = result?.payload?.onlinePurchPriceProfiles ?? [];
    const reordered = prioritizeOnlineProfileForView(
      freshProfiles,
      activeProfileId
    );
    const next = buildOnlineState(
      {
        ...result?.payload,
        onlinePurchPriceProfiles: reordered,
      },
      currentPartner?.country
    );
    updateOnlineState(next);
    initialOnlineRef.current = next;
    // Creating appends server-side; keep the persisted first id so a pending
    // local reorder remains dirty until the user explicitly saves it.
    savedActiveOnlineProfileIdRef.current = freshProfiles[0]?._id;
    return true;
  };

  // Persist every dirty section, then close. Details/Type validate through their
  // own mounted form, so if one is dirty on another tab, jump there first and
  // let the user save it. Bail (stay open) if a section fails validation.
  const saveAllAndClose = async () => {
    if (detailsDirty && step !== 0) {
      setClosePrompt(false);
      setStep(0);
      return;
    }
    if (typeDirty && step !== 1) {
      setClosePrompt(false);
      setStep(1);
      return;
    }
    if (detailsDirty && !(await commitDetails())) return setClosePrompt(false);
    if (typeDirty && !(await commitType())) return setClosePrompt(false);
    if (inPersonDirty && !(await commitInPerson()))
      return setClosePrompt(false);
    if (onlineDirty && !(await commitOnline())) return setClosePrompt(false);
    if (couplingDirty && !(await commitCoupling()))
      return setClosePrompt(false);
    setClosePrompt(false);
    close();
  };

  const discardAndClose = () => {
    setClosePrompt(false);
    close();
  };

  const primaryLabel = useMemo(() => {
    if (!isLastStep) return "Save & continue";
    return mode === "create" ? "Create partner" : "Done";
  }, [isLastStep, mode]);

  // Seed the Type form from the draft (a stable shape PartnerTypeStep maps back
  // to a card via optionIdFromPartner) so a re-mount restores the user's pick.
  const typeSeed: Partial<IPartner> = typeDraft.partnerTypeOption
    ? partnerFieldsFromOption(
        typeDraft.partnerTypeOption,
        typeDraft.softwareVendorId
      )
    : (currentPartner ?? {});

  const renderBody = () => {
    if (step === 0) {
      return (
        <PartnerDetailsStep
          form={detailsForm}
          mode={mode}
          editing={mode === "create"}
          details={mode === "edit" ? detailsDraft : draft}
          onValuesChange={
            mode === "edit"
              ? (_changed, all) =>
                  setDetailsDraft((prev) => ({ ...prev, ...all }))
              : undefined
          }
        />
      );
    }
    if (step === 1) {
      return (
        <PartnerTypeStep
          form={typeForm}
          mode={mode}
          partner={mode === "edit" ? typeSeed : draft}
        />
      );
    }
    if (step === 2) {
      return (
        <PartnerInPersonPricingStep
          value={inPerson}
          onChange={updateInPersonState}
          mode={mode}
          initial={
            mode === "edit"
              ? (initialInPersonRef.current ?? undefined)
              : undefined
          }
          profiles={
            mode === "edit"
              ? (currentPartner?.purchPriceProfiles ?? undefined)
              : undefined
          }
          creatingProfile={creatingRange}
          onProfileSelect={
            mode === "edit" ? handleInPersonProfileSelect : undefined
          }
          onProfileCreate={
            mode === "edit" ? handleInPersonProfileCreate : undefined
          }
          onNewRangeVisibleChange={handleInPersonNewRangeVisibleChange}
          showVendor={showVendorPricing}
          saveError={inPersonSaveError}
        />
      );
    }
    if (step === 3) {
      return (
        <PartnerOnlinePricingStep
          value={online}
          onChange={updateOnlineState}
          mode={mode}
          country={headerCountry}
          initial={
            mode === "edit"
              ? (initialOnlineRef.current ?? undefined)
              : undefined
          }
          profiles={
            mode === "edit"
              ? (currentPartner?.onlinePurchPriceProfiles ?? undefined)
              : undefined
          }
          creatingProfile={creatingRange}
          onProfileSelect={
            mode === "edit" ? handleOnlineProfileSelect : undefined
          }
          onProfileCreate={
            mode === "edit" ? handleOnlineProfileCreate : undefined
          }
          onNewRangeVisibleChange={setNewRangeOpen}
          showVendor={showVendorPricing}
          saveError={onlineSaveError}
          hasMerchantsSubmittedToAdyen={activeRangeSubmittedToAdyen}
        />
      );
    }
    return <PartnerCouplingStep value={coupling} onChange={setCoupling} />;
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={920}
      closeIcon={
        <CloseOutlined
          style={{ fontSize: 16, color: token.colorTextTertiary }}
        />
      }
      title={
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {PARTNER_WIZARD_STEPS[step].title}
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {headerName}
            {headerCountry && (
              <>
                {" · "}
                <CountryFlag
                  alpha2={headerCountry}
                  style={{ margin: "0 4px" }}
                />
                {countryFullName(headerCountry)}
              </>
            )}
          </Text>
        </div>
      }
    >
      <WizardStepper
        steps={PARTNER_WIZARD_STEPS}
        current={step}
        reachableUpTo={maxStepReached}
        dirtySteps={dirtySteps}
        onStepClick={goToStep}
        onBack={() => setStep((prev) => Math.max(0, prev - 1))}
        onForward={() => goToStep(Math.min(LAST_STEP, step + 1))}
      />

      {currentSectionDirty && (
        <Alert
          type="warning"
          showIcon
          message={
            step === 2 || step === 3
              ? "Price range changed"
              : "You have unsaved changes"
          }
          description={
            step === 2 || step === 3
              ? "Check that all merchants mapped to this partner have fees within the new range - any that fall outside must be updated manually in both their KBM profile and the Rapyd portal."
              : "Save this section to apply your changes."
          }
          action={
            step === 2 || step === 3 ? undefined : (
              <Space>
                <Button size="small" onClick={discardCurrentSection}>
                  Discard
                </Button>
                <Button
                  size="small"
                  type="primary"
                  loading={loading}
                  onClick={saveCurrentSection}
                >
                  Save changes
                </Button>
              </Space>
            )
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div style={{ paddingTop: 4 }}>{renderBody()}</div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 24,
          paddingTop: 16,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Button
          onClick={handleClose}
          style={{
            height: 40,
            padding: "0 24px",
            borderColor: token.colorPrimary,
            color: token.colorPrimary,
          }}
        >
          {mode === "edit" ? "Close" : "Cancel"}
        </Button>
        {mode === "create" && step === 3 && (
          <Button
            type="link"
            onClick={skipOnline}
            style={{ height: 40 }}
            icon={<ArrowRightOutlined />}
            iconPosition="end"
          >
            Skip online pricing setup
          </Button>
        )}
        {mode === "edit" ? (
          step !== 0 && (
            <Button
              type="primary"
              loading={loading}
              disabled={(step === 2 || step === 3) && newRangeOpen}
              onClick={!isLastStep ? handleSaveAndContinue : handleSave}
              icon={!isLastStep ? <ArrowRightOutlined /> : undefined}
              iconPosition={!isLastStep ? "end" : undefined}
              style={{ height: 40, padding: "0 24px" }}
            >
              {!isLastStep ? "Save & continue" : "Save"}
            </Button>
          )
        ) : (
          <Button
            type="primary"
            loading={loading}
            onClick={handlePrimary}
            icon={!isLastStep ? <ArrowRightOutlined /> : undefined}
            iconPosition="end"
            style={{ height: 40 }}
          >
            {primaryLabel}
          </Button>
        )}
      </div>

      <Modal
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        title="Unsaved changes"
        width={520}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() => setClosePrompt(false)}
              style={{ width: 130, height: 40 }}
            >
              Keep editing
            </Button>
            <Button
              danger
              onClick={discardAndClose}
              style={{ width: 130, height: 40 }}
            >
              Discard & close
            </Button>
            <Button
              type="primary"
              loading={loading}
              onClick={saveAllAndClose}
              style={{ width: 130, height: 40 }}
            >
              Save all & close
            </Button>
          </div>
        }
      >
        <Text>
          You have unsaved changes. Save them before closing, or discard and
          close.
        </Text>
      </Modal>
    </Modal>
  );
}
